import * as Sentry from '@sentry/react';
import { devLogger } from './dev-logger';

export class GeminiAudioManager {
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private audioQueue: ArrayBuffer[] = [];
  private readonly MAX_QUEUE = 800; // cap to avoid memory bloat
  private isPlaying = false;
  private nextStartTime = 0;
  private isMuted = false;
  private isSoftMuted = false;
  private pendingResponseCompletion = false;
  private currentAudioSources: AudioBufferSourceNode[] = [];
  private workletModuleLoaded = false;
  private frameSink: ((data: ArrayBuffer) => void) | null = null;
  
  // Legacy GeminiClient is no longer used; keep a minimal optional interface for back-compat
  constructor(private geminiClient: { state?: string; sendAudio?: (data: ArrayBuffer) => void; sendMuteState?: (muted: boolean) => void } | null) {}
  
  setClient(client: { state?: string; sendAudio?: (data: ArrayBuffer) => void; sendMuteState?: (muted: boolean) => void }): void {
    this.geminiClient = client;
  }

  // Optional: allow external sinks (e.g., Multimodal Live client) to consume mic frames.
  setFrameSink(cb: ((data: ArrayBuffer) => void) | null): void {
    this.frameSink = cb;
  }
  
  async startAudioCapture(): Promise<void> {
    this.audioContext = new AudioContext({ sampleRate: 16000 });
    this.workletModuleLoaded = false; // Reset module loaded flag for new context
    
    // Resume audio context if suspended (required by browser autoplay policies)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
      devLogger.log('🔊 Audio context resumed');
    }
    
    await this.connectAudioStream();
    devLogger.log('🎤 Audio capture started');
  }

  // Ensure an AudioContext exists for playback-only scenarios (no mic).
  async ensurePlaybackContext(): Promise<void> {
    if (!this.audioContext) {
      try {
        this.audioContext = new AudioContext({ sampleRate: 24000 });
      } catch (e) {
        // Fallback without explicit sample rate
        this.audioContext = new AudioContext();
      }
    }
    if (this.audioContext.state === 'suspended') {
      try { await this.audioContext.resume(); } catch {}
    }
  }

  private async connectAudioStream(): Promise<void> {
    if (this.workletNode) {
      this.disconnectAudioStream();
    }

    // Load the AudioWorklet processor (only once per AudioContext)
    if (!this.workletModuleLoaded) {
      try {
        await this.audioContext!.audioWorklet.addModule('/audio-worklet-processor.js');
        this.workletModuleLoaded = true;
        devLogger.log('✅ AudioWorklet module loaded successfully');
      } catch (error) {
        console.error('❌ Failed to load AudioWorklet processor:', error);
        throw error;
      }
    }

    this.mediaStream = await navigator.mediaDevices.getUserMedia({ 
      audio: { sampleRate: 16000, channelCount: 1 }
    });
    
    const source = this.audioContext!.createMediaStreamSource(this.mediaStream);
    
    // Create AudioWorkletNode
    this.workletNode = new AudioWorkletNode(this.audioContext!, 'gemini-audio-processor');
    
    // Listen for audio data and VAD signals from the worklet
    this.workletNode.port.onmessage = (event) => {
      const { type, data } = event.data;
      
      if (type === 'audioData') {
        if (this.frameSink) {
          // Forward to external sink (e.g., Multimodal Live input)
          this.frameSink(data);
        } else if (this.geminiClient && this.geminiClient.state === 'connected') {
          // Legacy: send to Gemini websocket
          this.geminiClient.sendAudio(data);
        }
      } else if (type === 'vad') {
        // Bubble up VAD state to UI via a custom event
        const speaking = !!data?.speaking;
        window.dispatchEvent(new CustomEvent('sb-vad-speaking', { detail: { speaking } }));
      }
    };
    
    // Update soft mute state in worklet
    this.workletNode.port.postMessage({
      type: 'setSoftMuted',
      data: { muted: this.isSoftMuted }
    });
    
    source.connect(this.workletNode);
    this.workletNode.connect(this.audioContext!.destination);
  }

  private disconnectAudioStream(): void {
    if (this.workletNode) {
      this.workletNode.disconnect();
      this.workletNode = null;
    }
    
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
  }
  
  async playAudio(audioData: ArrayBuffer): Promise<void> {
    if (!this.audioContext) {
      // Lazily create a playback context if none exists
      await this.ensurePlaybackContext();
    }
    
    // Resume audio context if suspended
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
      devLogger.log('🔊 Audio context resumed for playback');
    }
    
    // Add to queue with cap
    this.audioQueue.push(audioData);
    if (this.audioQueue.length > this.MAX_QUEUE) {
      // Drop oldest chunk(s)
      const drop = this.audioQueue.length - this.MAX_QUEUE;
      if (drop > 0) {
        this.audioQueue.splice(0, drop);
        try { Sentry.addBreadcrumb({ category: 'audio', level: 'warning', message: `audioQueue trimmed by ${drop}` }); } catch {}
      }
    }
    
    // Start playing if not already playing
    if (!this.isPlaying) {
      this.isPlaying = true;
      this.nextStartTime = this.audioContext.currentTime;
      this.playNextChunk();
    }
  }
  
  clearAudioQueue(): void {
    devLogger.log('🧹 Clearing audio queue due to connection issue');
    
    // Stop current sources
    this.currentAudioSources.forEach(source => {
      try {
        source.stop();
      } catch (error) {
        // Source might already be stopped, ignore error
      }
    });
    this.currentAudioSources = [];
    
    this.audioQueue = [];
    this.isPlaying = false;
    this.nextStartTime = 0;
  }
  
  stopCurrentAudio(): void {
    devLogger.log('🛑 Stopping current audio playback immediately');
    
    // Stop all currently playing audio sources
    this.currentAudioSources.forEach(source => {
      try {
        source.stop();
      } catch (error) {
        // Source might already be stopped, ignore error
      }
    });
    this.currentAudioSources = [];
    
    // Clear queue and reset playback state
    this.audioQueue = [];
    this.isPlaying = false;
    this.nextStartTime = 0;
  }
  
  private audioChunkCount = 0;
  
  private async playNextChunk(): Promise<void> {
    if (!this.audioContext || this.audioQueue.length === 0) {
      this.isPlaying = false;
      return;
    }

    const audioData = this.audioQueue.shift()!;
    
    try {
      // Check audio context state
      if (this.audioContext.state === 'suspended') {
        devLogger.warn('⚠️ Audio context suspended, attempting to resume...');
        await this.audioContext.resume();
      }
      
      // Gemini sends raw PCM data, not compressed audio
      // Convert Int16 PCM data to Float32 for Web Audio API
      const pcmData = new Int16Array(audioData);
      const sampleRate = 24000; // Gemini Live audio output sample rate
      
      if (pcmData.length === 0) {
        devLogger.warn('⚠️ Received empty audio data');
        this.playNextChunk(); // Continue with next chunk
        return;
      }
      
      // Create AudioBuffer for the PCM data
      const audioBuffer = this.audioContext.createBuffer(1, pcmData.length, sampleRate);
      const channelData = audioBuffer.getChannelData(0);
      
      // Convert Int16 PCM to Float32
      for (let i = 0; i < pcmData.length; i++) {
        channelData[i] = pcmData[i] / 32768.0; // Convert to -1.0 to 1.0 range
      }
      
      // Schedule playback at the correct time
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.audioContext.destination);
      
      // Track this source so we can stop it if needed
      this.currentAudioSources.push(source);
      
      // Ensure timing doesn't drift too far
      const currentTime = this.audioContext.currentTime;
      if (this.nextStartTime < currentTime) {
        this.nextStartTime = currentTime;
      }
      
      // Start at the scheduled time
      source.start(this.nextStartTime);
      
      // Calculate when this chunk will finish
      const duration = audioBuffer.duration;
      this.nextStartTime += duration;
      const drift = this.nextStartTime - this.audioContext.currentTime;
      if (this.audioChunkCount % 200 === 0) {
        devLogger.log(`⏱️ playback drift ${drift.toFixed(3)}s, queue=${this.audioQueue.length}`);
      }
      
      // Increment chunk counter but only log at large intervals to avoid console spam
      this.audioChunkCount++;
      if (this.audioChunkCount % 500 === 0) {
        devLogger.info(`🔊 Played ${this.audioChunkCount} audio chunks so far`);
      }
      
      // Schedule next chunk and clean up source reference
      source.onended = () => {
        // Remove this source from tracking
        const index = this.currentAudioSources.indexOf(source);
        if (index > -1) {
          this.currentAudioSources.splice(index, 1);
        }
        this.playNextChunk();
      };
      
    } catch (error) {
      console.error('❌ Error playing audio chunk:', error);
      try { Sentry.captureException(error as any, { tags: { phase: 'audio_playback' } }); } catch {}
      this.playNextChunk(); // Continue with next chunk even if this one failed
    }
  }
  
  async mute(): Promise<void> {
    if (this.isMuted) return;
    
    this.isMuted = true;
    this.isSoftMuted = true;
    devLogger.log('🔇 Audio input muted - sending silence data (preserves VAD and connection)');
    
    // Update soft mute state in worklet (sends silence instead of mic audio)
    if (this.workletNode) {
      this.workletNode.port.postMessage({
        type: 'setSoftMuted',
        data: { muted: true }
      });
    }
    
    // Signal backend about mute state but keep connection alive
    this.geminiClient?.sendMuteState(true);
  }
  

  
  async unmute(): Promise<void> {
    if (!this.isMuted) return;
    
    this.isMuted = false;
    this.isSoftMuted = false;
    devLogger.log('🎤 Audio input unmuted - resuming microphone audio stream');
    
    try {
      // Update soft mute state in worklet (resume sending mic audio)
      if (this.workletNode) {
        this.workletNode.port.postMessage({
          type: 'setSoftMuted',
          data: { muted: false }
        });
      }
      
      // Signal backend about unmute state
      this.geminiClient?.sendMuteState(false);
    } catch (error) {
      console.error('❌ Error resuming audio stream after unmute:', error);
      this.isMuted = true; // Revert to muted state on error
      this.isSoftMuted = false;
      throw error;
    }
  }
  
  get muted(): boolean {
    return this.isMuted;
  }
  
  stop(): void {
    // Stop all current audio sources first
    this.stopCurrentAudio();
    
    // Cleanup audio processing
    this.disconnectAudioStream();
    this.audioContext?.close();
    
    // Reset all state
    this.audioQueue = [];
    this.isPlaying = false;
    this.isMuted = false;
    this.isSoftMuted = false;
    this.pendingResponseCompletion = false;
    this.currentAudioSources = [];
    this.audioContext = null;
  }
} 
